import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import ApiService from '../../services/api';

interface QualityParameter {
  id: string;
  name: string;
  specification: string;
  measuredValue: string;
  tolerance: string;
  status: 'pass' | 'fail' | 'warning';
  unit: string;
}

interface QualityCheck {
  id: string;
  checkNumber: string;
  workOrderId: string;
  productCode: string;
  productName: string;
  inspector: string;
  checkDate: string;
  status: 'pending' | 'in_progress' | 'passed' | 'failed' | 'on_hold';
  checkType: 'incoming' | 'in_process' | 'final' | 'audit';
  parameters: QualityParameter[];
  overallResult: 'pass' | 'fail' | 'pending';
  notes?: string;
}

interface DefectRecord {
  id: string;
  workOrderId: string;
  productCode: string;
  defectType: string;
  defectDescription: string;
  severity: 'minor' | 'major' | 'critical';
  quantity: number;
  reportedBy: string;
  reportedDate: string;
  status: 'open' | 'investigating' | 'resolved' | 'closed';
  rootCause?: string;
  correctiveAction?: string;
}

interface QualityMetrics {
  passRate: number;
  defectRate: number;
  totalChecks: number;
  failedChecks: number;
}

interface ControlPlan {
  id: string;
  planName: string;
  itemId: string;
  version: string;
  status: 'active' | 'inactive';
  description?: string;
  createdAt: string;
}

interface QualityState {
  qualityChecks: QualityCheck[];
  defectRecords: DefectRecord[];
  defectTypes: any[];
  metrics: QualityMetrics | null;
  selectedCheck: QualityCheck | null;
  controlPlans: ControlPlan[];
  controlPlansLoading: boolean;
  isLoading: boolean;
  error: string | null;
}


// Normalise a raw API inspection record into our QualityCheck shape
function normaliseCheck(raw: any): QualityCheck {
  return {
    id: String(raw.id ?? raw._id ?? ''),
    checkNumber: raw.checkNumber ?? raw.inspectionId ?? raw.check_number ?? `QC-${raw.id}`,
    workOrderId: raw.workOrderId ?? raw.workOrder ?? raw.work_order_id ?? '',
    productCode: raw.productCode ?? raw.product ?? raw.product_code ?? '',
    productName: raw.productName ?? raw.product_name ?? '',
    inspector: raw.inspector ?? raw.inspectedBy ?? '',
    checkDate: raw.checkDate ?? raw.dateTime ?? raw.check_date ?? '',
    status: raw.status ?? 'pending',
    checkType: raw.checkType ?? raw.inspectionType ?? raw.check_type ?? 'in_process',
    parameters: Array.isArray(raw.parameters) ? raw.parameters : [],
    overallResult: raw.overallResult ?? raw.result ?? raw.overall_result ?? 'pending',
    notes: raw.notes,
  };
}

// Normalise a raw API defect record into our DefectRecord shape
const prettyEnum = (value: any): string =>
  typeof value === 'string' ? value.replace(/_/g, ' ') : '';

function normaliseDefect(raw: any): DefectRecord {
  const reportedDate =
    raw.reportedDate ?? raw.dateTime ?? raw.reported_date ?? raw.createdAt ?? raw.created_at ?? '';
  return {
    id: String(raw.id ?? raw._id ?? ''),
    workOrderId: raw.workOrderId ?? raw.workOrder ?? raw.work_order_id ?? '',
    productCode:
      raw.productCode ?? raw.product ?? raw.product_code ??
      raw.itemCode ?? raw.item_code ?? raw.itemName ?? raw.item_name ?? '',
    defectType:
      raw.defectType ?? raw.type ?? raw.defect_type ??
      (prettyEnum(raw.category) || prettyEnum(raw.nonconformanceType) || ''),
    defectDescription: raw.defectDescription ?? raw.description ?? raw.defect_description ?? '',
    severity: raw.severity ?? 'minor',
    quantity: Number(raw.quantity ?? raw.quantityAffected ?? 1),
    reportedBy:
      raw.reportedByName ?? raw.reported_by_name ??
      raw.reportedBy ?? raw.inspector ?? raw.reported_by ?? '',
    reportedDate: reportedDate ? String(reportedDate).slice(0, 10) : '',
    status: raw.status ?? 'open',
    rootCause: raw.rootCause ?? raw.root_cause,
    correctiveAction: raw.correctiveAction ?? raw.corrective_action,
  };
}

function normaliseControlPlan(raw: any): ControlPlan {
  return {
    id: String(raw.id ?? raw._id ?? ''),
    planName: raw.planName ?? raw.plan_name ?? raw.name ?? '',
    itemId: raw.itemId ?? raw.item_id ?? raw.productId ?? '',
    version: raw.version ?? '1.0',
    status: raw.status === 'active' ? 'active' : 'inactive',
    description: raw.description,
    createdAt: raw.createdAt ?? raw.created_at ?? '',
  };
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

const initialState: QualityState = {
  qualityChecks: [],
  defectRecords: [],
  defectTypes: [],
  metrics: null,
  selectedCheck: null,
  controlPlans: [],
  controlPlansLoading: false,
  isLoading: false,
  error: null,
};

export const fetchQualityChecks = createAsyncThunk(
  'quality/fetchChecks',
  async (_, { rejectWithValue }) => {
    try {
      const data = await ApiService.getQualityInspections();
      const raw: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.inspections)
        ? data.inspections
        : Array.isArray(data?.data)
        ? data.data
        : [];
      return raw.map(normaliseCheck);
    } catch (error: any) {
      return rejectWithValue(error?.message ?? 'Failed to fetch quality checks');
    }
  }
);

export const fetchDefectRecords = createAsyncThunk(
  'quality/fetchDefects',
  async (_, { rejectWithValue }) => {
    try {
      const data = await ApiService.getDefects();
      const raw: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.defects)
        ? data.defects
        : Array.isArray(data?.data)
        ? data.data
        : [];
      return raw.map(normaliseDefect);
    } catch (error: any) {
      return rejectWithValue(error?.message ?? 'Failed to fetch defect records');
    }
  }
);

export const fetchDefectTypes = createAsyncThunk(
  'quality/fetchDefectTypes',
  async () => {
    try {
      const data = await ApiService.getDefectTypes();
      const raw: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.defectTypes)
        ? data.defectTypes
        : Array.isArray(data?.data)
        ? data.data
        : [];
      return raw;
    } catch {
      return [];
    }
  }
);

export const fetchQualityMetrics = createAsyncThunk(
  'quality/fetchMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const data = await ApiService.getDefectAnalytics();
      if (data) {
        return {
          passRate: data.passRate ?? data.pass_rate ?? 0,
          defectRate: data.defectRate ?? data.defect_rate ?? 0,
          totalChecks: data.totalChecks ?? data.total_checks ?? 0,
          failedChecks: data.failedChecks ?? data.failed_checks ?? 0,
        } as QualityMetrics;
      }
      return null;
    } catch (error: any) {
      return rejectWithValue(error?.message ?? 'Failed to fetch quality metrics');
    }
  }
);

export const createInspection = createAsyncThunk(
  'quality/createInspection',
  async (inspectionData: Partial<QualityCheck>) => {
    const data = await ApiService.createQualityInspection(inspectionData);
    return normaliseCheck(data ?? inspectionData);
  }
);

export const createDefect = createAsyncThunk(
  'quality/createDefect',
  async (defectData: Partial<DefectRecord>) => {
    const data = await ApiService.createDefect(defectData);
    return normaliseDefect(data ?? defectData);
  }
);

export const updateDefectStatus = createAsyncThunk(
  'quality/updateDefectStatus',
  async ({ id, status }: { id: string; status: DefectRecord['status'] }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE_URL}/quality/defects/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return rejectWithValue(body?.message ?? `HTTP ${res.status}`);
      }
      return { id, status };
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Failed to update defect status');
    }
  }
);

export const fetchControlPlans = createAsyncThunk(
  'quality/fetchControlPlans',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE_URL}/quality/control-plans`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return rejectWithValue(body?.message ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      const raw: any[] = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      return raw.map(normaliseControlPlan);
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Failed to fetch control plans');
    }
  }
);

export const createControlPlan = createAsyncThunk(
  'quality/createControlPlan',
  async (planData: { planName: string; itemId: string; version: string; description?: string }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE_URL}/quality/control-plans`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(planData),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return rejectWithValue(body?.message ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      return normaliseControlPlan(data?.data ?? data);
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Failed to create control plan');
    }
  }
);

export const updateControlPlan = createAsyncThunk(
  'quality/updateControlPlan',
  async ({ id, ...fields }: { id: string; status?: 'active' | 'inactive'; planName?: string; itemId?: string; version?: string; description?: string }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE_URL}/quality/control-plans/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(fields),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return rejectWithValue(body?.message ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      return normaliseControlPlan(data?.data ?? data);
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Failed to update control plan');
    }
  }
);

const qualitySlice = createSlice({
  name: 'quality',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedCheck: (state, action: PayloadAction<QualityCheck | null>) => {
      state.selectedCheck = action.payload;
    },
    updateCheckStatus: (state, action: PayloadAction<{ id: string; status: QualityCheck['status'] }>) => {
      const { id, status } = action.payload;
      const check = state.qualityChecks.find(qc => qc.id === id);
      if (check) {
        check.status = status;
      }
    },
    updateDefectStatusLocal: (state, action: PayloadAction<{ id: string; status: DefectRecord['status'] }>) => {
      const { id, status } = action.payload;
      const defect = state.defectRecords.find(dr => dr.id === id);
      if (defect) {
        defect.status = status;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQualityChecks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQualityChecks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.qualityChecks = action.payload;
      })
      .addCase(fetchQualityChecks.rejected, (state, action) => {
        state.isLoading = false;
        const message = typeof action.payload === 'string' ? action.payload : action.error.message;
        state.error = message ?? 'Failed to load quality checks';
        state.qualityChecks = [];
      })
      .addCase(fetchDefectRecords.fulfilled, (state, action) => {
        state.defectRecords = action.payload;
      })
      .addCase(fetchDefectRecords.rejected, (state, action) => {
        const message = typeof action.payload === 'string' ? action.payload : action.error.message;
        state.error = message ?? 'Failed to load defect records';
        state.defectRecords = [];
      })
      .addCase(fetchDefectTypes.fulfilled, (state, action) => {
        state.defectTypes = action.payload;
      })
      .addCase(fetchQualityMetrics.fulfilled, (state, action) => {
        state.metrics = action.payload;
      })
      .addCase(fetchQualityMetrics.rejected, (state, action) => {
        const message = typeof action.payload === 'string' ? action.payload : action.error.message;
        state.error = message ?? 'Failed to load quality metrics';
        state.metrics = null;
      })
      .addCase(createInspection.fulfilled, (state, action) => {
        state.qualityChecks.unshift(action.payload);
      })
      .addCase(createDefect.fulfilled, (state, action) => {
        state.defectRecords.unshift(action.payload);
      })
      .addCase(updateDefectStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const defect = state.defectRecords.find((d) => d.id === id);
        if (defect) defect.status = status;
      })
      .addCase(fetchControlPlans.pending, (state) => {
        state.controlPlansLoading = true;
      })
      .addCase(fetchControlPlans.fulfilled, (state, action) => {
        state.controlPlansLoading = false;
        state.controlPlans = action.payload;
      })
      .addCase(fetchControlPlans.rejected, (state, action) => {
        state.controlPlansLoading = false;
        const message = typeof action.payload === 'string' ? action.payload : action.error.message;
        state.error = message ?? 'Failed to load control plans';
      })
      .addCase(createControlPlan.fulfilled, (state, action) => {
        state.controlPlans.unshift(action.payload);
      })
      .addCase(updateControlPlan.fulfilled, (state, action) => {
        const idx = state.controlPlans.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.controlPlans[idx] = action.payload;
      });
  },
});

export const { clearError, setSelectedCheck, updateCheckStatus, updateDefectStatusLocal } = qualitySlice.actions;
export default qualitySlice.reducer;
